import { useEffect, useState } from 'react';
import { getSeguimientoPoliza, upsertSeguimientoPoliza } from '../services/api';

const TIPOS_CONTACTO = ['Llamada', 'WhatsApp', 'Email', 'Visita', 'Otro'];

interface Props {
  polizaId: number;
  numeroPoliza: string;
  contratante: string;
  onClose: () => void;
  onSaved?: (enSeguimiento: boolean) => void;
}

interface Seguimiento {
  enSeguimiento: boolean;
  tipoContacto: string | null;
  notas: string | null;
  fechaUltimoContacto: string | null;
}

export default function SeguimientoModal({ polizaId, numeroPoliza, contratante, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Seguimiento>({
    enSeguimiento: false,
    tipoContacto: null,
    notas: null,
    fechaUltimoContacto: null,
  });

  useEffect(() => {
    getSeguimientoPoliza(polizaId)
      .then(r => setForm({
        enSeguimiento: r.data.enSeguimiento ?? false,
        tipoContacto: r.data.tipoContacto ?? null,
        notas: r.data.notas ?? null,
        fechaUltimoContacto: r.data.fechaUltimoContacto
          ? r.data.fechaUltimoContacto.split('T')[0]
          : null,
      }))
      .finally(() => setLoading(false));
  }, [polizaId]);

  const save = async () => {
    setSaving(true);
    try {
      await upsertSeguimientoPoliza(polizaId, {
        enSeguimiento: form.enSeguimiento,
        tipoContacto: form.tipoContacto || null,
        notas: form.notas || null,
        fechaUltimoContacto: form.fechaUltimoContacto || null,
      });
      onSaved?.(form.enSeguimiento);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Seguimiento de cobranza</h3>
            <p className="text-sm text-gray-500 font-mono mt-0.5">{numeroPoliza}</p>
            <p className="text-sm text-gray-600">{contratante}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            Cargando...
          </div>
        ) : (
          <div className="space-y-4">
            {/* En seguimiento checkbox */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enSeguimiento}
                onChange={e => setForm(f => ({ ...f, enSeguimiento: e.target.checked }))}
                className="h-4 w-4 rounded"
              />
              <span className="text-sm font-medium text-gray-700">En seguimiento</span>
            </label>

            {/* Tipo de contacto */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de contacto</label>
              <div className="flex flex-wrap gap-2">
                {TIPOS_CONTACTO.map(t => (
                  <button
                    key={t}
                    onClick={() => setForm(f => ({ ...f, tipoContacto: f.tipoContacto === t ? null : t }))}
                    className={`px-3 py-1 rounded-full text-xs border transition-all ${
                      form.tipoContacto === t
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 text-gray-600 hover:border-primary-400'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Fecha último contacto */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fecha último contacto</label>
              <input
                type="date"
                value={form.fechaUltimoContacto ?? ''}
                onChange={e => setForm(f => ({ ...f, fechaUltimoContacto: e.target.value || null }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-full"
              />
            </div>

            {/* Notas */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Notas</label>
              <textarea
                value={form.notas ?? ''}
                onChange={e => setForm(f => ({ ...f, notas: e.target.value || null }))}
                rows={3}
                placeholder="Observaciones, acuerdos, pendientes..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
